import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Mail, Clock, CheckCircle, XCircle, RefreshCw, Trash2, User, X, Eye, ChevronRight } from 'lucide-react';
import { Invitation, UserRole, User as UserType } from '../types';
import { apiService } from '../services/api';

interface InvitationsListProps {
  invitations: Invitation[];
  roles: UserRole[];
  businessId: string;
  onRefresh: () => void;
}

const InvitationsList: React.FC<InvitationsListProps> = ({
  invitations,
  roles,
  businessId,
  onRefresh
}) => {
  const [loadingStates, setLoadingStates] = useState<{ [key: string]: string }>({});
  const [currentUser, setCurrentUser] = useState<UserType | null>(null);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [invitationToRevoke, setInvitationToRevoke] = useState<Invitation | null>(null);
  const [showInvitationsModal, setShowInvitationsModal] = useState(false);
  const [modalView, setModalView] = useState<'pending' | 'expired'>('pending');

  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const user = await apiService.getCurrentUser();
        setCurrentUser(user);
      } catch (error) {
        console.error('Error loading current user:', error);
      }
    };
    loadCurrentUser();
  }, []);

  const openInvitationsModal = (view: 'pending' | 'expired') => {
    if (view === 'pending' && activePendingInvitations.length === 0) return;
    if (view === 'expired' && expiredInvitations.length === 0) return;
    setModalView(view);
    setShowInvitationsModal(true);
  };

  const getEffectiveStatus = (invitation: Invitation): Invitation['status'] => {
    if (invitation.status === 'PENDING') {
      const expiresAtMs = new Date(invitation.expiresAt).getTime();
      if (!Number.isNaN(expiresAtMs) && expiresAtMs <= Date.now()) {
        return 'EXPIRED';
      }
    }
    return invitation.status;
  };

  // Close invitations modal if there are no more invitations for the current view
  useEffect(() => {
    if (!showInvitationsModal) return;

    const pendingInvitations = invitations.filter(invitation => invitation.status === 'PENDING');
    const activePendingInvitations = pendingInvitations.filter(
      (invitation) => getEffectiveStatus(invitation) === 'PENDING'
    );
    const expiredInvitations = pendingInvitations.filter(
      (invitation) => getEffectiveStatus(invitation) === 'EXPIRED'
    );

    const invitationsForView = modalView === 'pending' ? activePendingInvitations : expiredInvitations;
    if (invitationsForView.length === 0) {
      setShowInvitationsModal(false);
    }
  }, [invitations, modalView, showInvitationsModal]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showInvitationsModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showInvitationsModal]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'EXPIRED':
        return <Clock className="h-4 w-4 text-gray-500" />;
      case 'ACCEPTED':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'REVOKED':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Mail className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Pending';
      case 'EXPIRED':
        return 'Expired';
      case 'ACCEPTED':
        return 'Accepted';
      case 'REVOKED':
        return 'Revoked';
      default:
        return 'Unknown';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'text-yellow-600 bg-yellow-50';
      case 'EXPIRED':
        return 'text-gray-600 bg-gray-100';
      case 'ACCEPTED':
        return 'text-green-600 bg-green-50';
      case 'REVOKED':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getRoleNames = (roleIds: string[]) => {
    if (!roleIds || roleIds.length === 0) return '';
    
    const matchedRoles = roles.filter(role => roleIds.includes(role.id));
    if (matchedRoles.length === 0) return '';
    
    return matchedRoles
      .map(role => {
        return role.role || role.roleDescription || 'Unknown Role';
      })
      .join(', ');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleResend = async (invitationId: string) => {
    setLoadingStates(prev => ({ ...prev, [invitationId]: 'resending' }));
    try {
      const inviterName = currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'The business owner';
      await apiService.resendInvitation(businessId, invitationId, inviterName);
      onRefresh();
    } catch (error) {
      console.error('Error resending invitation:', error);
    } finally {
      setLoadingStates(prev => ({ ...prev, [invitationId]: '' }));
    }
  };

  const handleRevoke = (invitation: Invitation) => {
    setInvitationToRevoke(invitation);
    setShowRevokeModal(true);
  };

  const handleRevokeConfirm = async () => {
    if (!invitationToRevoke) return;

    setLoadingStates(prev => ({ ...prev, [invitationToRevoke.id]: 'revoking' }));
    try {
      await apiService.revokeInvitation(businessId, invitationToRevoke.id);
      onRefresh();
    } catch (error) {
      console.error('Error revoking invitation:', error);
    } finally {
      setLoadingStates(prev => ({ ...prev, [invitationToRevoke.id]: '' }));
      setShowRevokeModal(false);
      setInvitationToRevoke(null);
    }
  };

  const handleRevokeCancel = () => {
    setShowRevokeModal(false);
    setInvitationToRevoke(null);
  };

  // Filter to only show invites that haven't been accepted/revoked.
  // Note: backend stores expired invites as status=PENDING, so we compute an effective status in the UI.
  const pendingInvitations = invitations.filter(invitation => invitation.status === 'PENDING');
  const activePendingInvitations = pendingInvitations.filter(
    (invitation) => getEffectiveStatus(invitation) === 'PENDING'
  );
  const expiredInvitations = pendingInvitations.filter(
    (invitation) => getEffectiveStatus(invitation) === 'EXPIRED'
  );

  // If there are pending invitations, show summary card with View button
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${expiredInvitations.length === 0 ? 'md:col-span-2' : ''} ${activePendingInvitations.length > 0 ? 'cursor-pointer md:cursor-default' : ''}`}
          role={activePendingInvitations.length > 0 ? 'button' : undefined}
          tabIndex={activePendingInvitations.length > 0 ? 0 : -1}
          onClick={() => openInvitationsModal('pending')}
          onKeyDown={(e) => {
            if (activePendingInvitations.length === 0) return;
            if (e.key === 'Enter' || e.key === ' ') openInvitationsModal('pending');
          }}
          aria-disabled={activePendingInvitations.length === 0}
        >
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center">
                <Mail className="h-6 w-6 text-gray-400" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              {activePendingInvitations.length === 0 ? (
                <>
                  <p className="text-sm font-medium text-gray-700">No pending invitations</p>
                  <p className="text-xs text-gray-500 mt-1">Invitations you send to employees and professionals will show here.</p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-gray-700">Pending invitations</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {activePendingInvitations.length} {activePendingInvitations.length === 1 ? 'invitation is' : 'invitations are'} waiting to be accepted.
                  </p>
                </>
              )}
            </div>
            {activePendingInvitations.length > 0 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openInvitationsModal('pending');
                  }}
                  className="hidden md:flex px-4 py-2 text-sm font-medium text-[#0B4778] bg-white border border-[#0B4778] rounded-lg hover:bg-[#0B4778] hover:text-white transition-colors items-center justify-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  <span>View</span>
                </button>
                <div className="md:hidden flex-shrink-0 text-gray-400">
                  <ChevronRight className="h-5 w-5" />
                </div>
              </>
            )}
          </div>
        </div>

        {expiredInvitations.length > 0 && (
          <div
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer md:cursor-default"
            role="button"
            tabIndex={0}
            onClick={() => openInvitationsModal('expired')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') openInvitationsModal('expired');
            }}
          >
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                  <Clock className="h-6 w-6 text-gray-400" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700">Expired invitations</p>
                <p className="text-xs text-gray-500 mt-1">
                  {expiredInvitations.length} {expiredInvitations.length === 1 ? 'invitation has' : 'invitations have'} expired.
                </p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openInvitationsModal('expired');
                }}
                className="hidden md:flex px-4 py-2 text-sm font-medium text-[#0B4778] bg-white border border-[#0B4778] rounded-lg hover:bg-[#0B4778] hover:text-white transition-colors items-center justify-center gap-2"
              >
                <Eye className="h-4 w-4" />
                <span>View</span>
              </button>
              <div className="md:hidden flex-shrink-0 text-gray-400">
                <ChevronRight className="h-5 w-5" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Invitations Modal - Rendered via Portal */}
      {showInvitationsModal && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4" 
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            margin: 0,
            padding: '1rem'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowInvitationsModal(false);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {modalView === 'pending' ? 'Pending Invitations' : 'Expired Invitations'}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {modalView === 'pending'
                    ? `${activePendingInvitations.length} ${activePendingInvitations.length === 1 ? 'invitation' : 'invitations'} pending`
                    : `${expiredInvitations.length} ${expiredInvitations.length === 1 ? 'invitation' : 'invitations'} expired`}
                </p>
              </div>
              <button
                onClick={() => setShowInvitationsModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="divide-y divide-gray-200">
                {(modalView === 'pending' ? activePendingInvitations : expiredInvitations).map((invitation) => (
                  <div key={invitation.id} className="p-6">
                    {(() => {
                      const effectiveStatus = getEffectiveStatus(invitation);
                      return (
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="font-medium text-gray-900">{invitation.email}</span>
                          </div>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(effectiveStatus)} flex items-center`}>
                            {getStatusIcon(effectiveStatus)}
                            <span className="ml-1">{getStatusText(effectiveStatus)}</span>
                          </span>
                        </div>
                        
                        {invitation.roleIds && invitation.roleIds.length > 0 && (
                          <div className="text-sm text-gray-700 mb-2">
                            <span className="font-medium">Role:</span>{' '}
                            <span className="text-gray-600">
                              {invitation.roleIds.map((id: string) => {
                                const role = roles.find(r => r.id === id);
                                return role?.role || role?.roleDescription || 'Unknown Role';
                              }).join(', ')}
                            </span>
                          </div>
                        )}
                        
                        <div className="text-xs text-gray-500 space-y-1">
                          <div>Sent: {formatDate(invitation.createdAt)}</div>
                          <div>Expires: {formatDate(invitation.expiresAt)}</div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => handleResend(invitation.id)}
                          disabled={loadingStates[invitation.id] === 'resending'}
                          className="p-2 text-gray-400 hover:text-blue-600 transition-colors disabled:opacity-50"
                          title="Resend invitation"
                        >
                          {loadingStates[invitation.id] === 'resending' ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleRevoke(invitation)}
                          disabled={loadingStates[invitation.id] === 'revoking'}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                          title="Remove invitation"
                        >
                          {loadingStates[invitation.id] === 'revoking' ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        ,
        document.body
      )}

      {/* Revoke Invitation Confirmation Modal */}
      {showRevokeModal && invitationToRevoke && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[10050] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="w-6 h-6 text-red-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Revoke Invitation</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone.</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700">
                  Are you sure you want to revoke the invitation sent to <strong>"{invitationToRevoke.email}"</strong>?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  The recipient will no longer be able to accept this invitation. This action cannot be undone.
                </p>
              </div>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={handleRevokeCancel}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={handleRevokeConfirm}
                  disabled={loadingStates[invitationToRevoke.id] === 'revoking'}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>{loadingStates[invitationToRevoke.id] === 'revoking' ? 'Revoking...' : 'Revoke Invitation'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InvitationsList; 